import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { getAdminStatus } from '../lib/apiClient';
import { useAuth } from './AuthContext';

type AdminAccessContextValue = {
  isAdmin: boolean;
  isLoading: boolean;
  refresh: () => Promise<void>;
};

const AdminAccessContext = createContext<AdminAccessContextValue | undefined>(undefined);
const E2E_ADMIN_KEY = 'taccounts_e2e_is_admin';

function resolveLocalE2eAdminFlag(): boolean | null {
  if (window.location.hostname !== 'localhost') {
    return null;
  }

  const raw = window.localStorage.getItem(E2E_ADMIN_KEY);
  if (!raw) {
    return null;
  }

  const normalized = raw.trim().toLowerCase();
  if (normalized === 'true' || normalized === '1' || normalized === 'yes') {
    return true;
  }

  if (normalized === 'false' || normalized === '0' || normalized === 'no') {
    return false;
  }

  return null;
}

export function useAdminAccess(): AdminAccessContextValue {
  const context = useContext(AdminAccessContext);
  if (!context) {
    throw new Error('useAdminAccess must be used within an AdminAccessProvider');
  }

  return context;
}

type AdminAccessProviderProps = {
  children: ReactNode;
};

export function AdminAccessProvider({ children }: AdminAccessProviderProps) {
  const { user, loading } = useAuth();
  const localE2eAdmin = resolveLocalE2eAdminFlag();
  const [isAdmin, setIsAdmin] = useState(() => localE2eAdmin ?? false);
  const [isLoading, setIsLoading] = useState(() => localE2eAdmin === null);

  const fetchAdminAccess = useCallback(async (): Promise<boolean> => {
    const result = await getAdminStatus({
      shouldShowError: (error) => error.code !== 'UNAUTHORIZED',
    });

    if (!result.ok) {
      return false;
    }

    return Boolean(result.data.is_admin);
  }, []);

  useEffect(() => {
    let isCancelled = false;

    if (localE2eAdmin !== null) {
      setIsAdmin(localE2eAdmin);
      setIsLoading(false);
      return () => {
        isCancelled = true;
      };
    }

    if (loading) {
      return () => {
        isCancelled = true;
      };
    }

    if (!user) {
      setIsAdmin(false);
      setIsLoading(false);
      return () => {
        isCancelled = true;
      };
    }

    const load = async () => {
      setIsLoading(true);
      const nextIsAdmin = await fetchAdminAccess();

      if (!isCancelled) {
        setIsAdmin(nextIsAdmin);
        setIsLoading(false);
      }
    };

    void load();

    return () => {
      isCancelled = true;
    };
  }, [fetchAdminAccess, loading, localE2eAdmin, user]);

  const refresh = useCallback(async () => {
    if (localE2eAdmin !== null) {
      setIsAdmin(localE2eAdmin);
      setIsLoading(false);
      return;
    }

    if (!user) {
      setIsAdmin(false);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const nextIsAdmin = await fetchAdminAccess();
    setIsAdmin(nextIsAdmin);
    setIsLoading(false);
  }, [fetchAdminAccess, localE2eAdmin, user]);

  const value = useMemo<AdminAccessContextValue>(
    () => ({
      isAdmin,
      isLoading,
      refresh,
    }),
    [isAdmin, isLoading, refresh]
  );

  return <AdminAccessContext.Provider value={value}>{children}</AdminAccessContext.Provider>;
}
